import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { PLAYLIST } from "@/lib/playlist";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Music2, MessageSquareHeart, Search } from "lucide-react";

function fmt(s) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60), r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

export default function PlaylistPage() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [query, setQuery] = useState("");
  const audioRef = useRef(null);
  const playedRef = useRef(new Set());

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return PLAYLIST.map((s, i) => ({ ...s, i }));
    return PLAYLIST.map((s, i) => ({ ...s, i })).filter(s => `${s.t} ${s.a}`.toLowerCase().includes(q));
  }, [query]);

  const current = PLAYLIST[idx];

  const nextIndex = () => {
    if (!shuffle) return (idx + 1) % PLAYLIST.length;
    playedRef.current.add(idx);
    if (playedRef.current.size >= PLAYLIST.length) playedRef.current = new Set([idx]);
    const remaining = PLAYLIST.map((_, i) => i).filter(i => !playedRef.current.has(i));
    return remaining[Math.floor(Math.random() * remaining.length)];
  };

  useEffect(() => {
    const audio = new Audio(current.src);
    audioRef.current = audio;
    audio.volume = 0.7;
    const onTime = () => { setProgress(audio.currentTime); setDuration(audio.duration || 0); };
    const onEnded = () => setIdx(nextIndex());
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);
    if (playing) audio.play().catch(() => setPlaying(false));
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  }, [playing]);

  const pick = (i) => { setIdx(i); setPlaying(true); };
  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-4xl mx-auto px-6 py-14" data-testid="playlist-page">
        <div className="text-veil">
          <div className="text-sm uppercase tracking-[0.32em] mb-3 font-bold">
            <span className="text-[#10B981]">Welp</span> <span className="text-[#5A5A5A]">·</span> <span className="text-[#FF1A82]">Dating With Integrity</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A]">The Welp <span className="italic text-[#10B981]">Playlist.</span></h1>
          <p className="mt-4 text-lg text-[#1A1A1A] font-semibold max-w-2xl">{PLAYLIST.length} songs for every stage of the heart — heartbreak, healing, and bouncing back.</p>
        </div>

        {/* Player */}
        <div className="mt-8 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border-2 border-[#10B981]/40 p-7 sticky top-20 z-20" data-testid="playlist-player">
          <div className="flex items-center gap-3">
            <Music2 className="w-6 h-6 text-[#FF1A82] shrink-0" />
            <div className="min-w-0">
              <div className="text-xl font-bold text-[#1A1A1A] truncate" data-testid="now-playing-title">{current.t}</div>
              {current.a && <div className="text-lg text-[#5A5A5A] font-semibold truncate">{current.a}</div>}
            </div>
          </div>
          <div className="mt-4 h-3 rounded-full bg-black/10 cursor-pointer" onClick={seek} data-testid="seek-bar">
            <div className="h-3 rounded-full bg-[#10B981]" style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }} />
          </div>
          <div className="flex justify-between text-lg text-[#5A5A5A] font-semibold mt-1">
            <span>{fmt(progress)}</span><span>{fmt(duration)}</span>
          </div>
          <div className="mt-4 flex items-center justify-center gap-4">
            <button onClick={() => setShuffle(s => !s)} title="Shuffle (never repeats until every song has played)" aria-pressed={shuffle}
              className={`p-3 rounded-full border-2 transition ${shuffle ? "border-[#10B981] bg-[#10B981]/15 text-[#10B981]" : "border-black/10 text-[#5A5A5A] hover:border-[#10B981]/50"}`}
              data-testid="shuffle-btn">
              <Shuffle className="w-6 h-6" />
            </button>
            <button onClick={() => pick((idx - 1 + PLAYLIST.length) % PLAYLIST.length)} className="p-3 rounded-full border-2 border-black/10 text-[#1A1A1A] hover:border-[#FF1A82]/50 transition" data-testid="prev-btn">
              <SkipBack className="w-6 h-6" />
            </button>
            <button onClick={() => setPlaying(p => !p)} className="p-5 rounded-full bg-[#10B981] text-white shadow-lg hover:brightness-110 transition" data-testid="play-pause-btn">
              {playing ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-0.5" />}
            </button>
            <button onClick={() => pick(nextIndex())} className="p-3 rounded-full border-2 border-black/10 text-[#1A1A1A] hover:border-[#FF1A82]/50 transition" data-testid="next-btn">
              <SkipForward className="w-6 h-6" />
            </button>
          </div>
          {shuffle && <p className="mt-3 text-lg text-[#10B981] font-bold text-center">Shuffle on — no song repeats until all {PLAYLIST.length} have played.</p>}
        </div>

        {/* Suggest a song */}
        <div className="mt-6 rounded-2xl border-2 border-[#FF1A82]/40 bg-white/70 backdrop-blur-lg p-5 flex items-start gap-3" data-testid="suggest-song-note">
          <MessageSquareHeart className="w-6 h-6 text-[#FF1A82] shrink-0 mt-0.5" />
          <p className="text-lg text-[#1A1A1A] font-semibold">
            Want a song added to the Welp Playlist? Send your suggestion through the{" "}
            <Link to="/feedback" className="text-[#FF1A82] underline font-bold" data-testid="suggest-song-link">Feedback page</Link> — we read every one.
          </p>
        </div>

        {/* Track list */}
        <div className="mt-6 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-5">
          <div className="flex items-center gap-2 border border-black/15 rounded-xl px-4 py-2.5 bg-white/60">
            <Search className="w-5 h-5 text-[#5A5A5A]" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a song or artist…"
              className="w-full bg-transparent outline-none text-lg text-[#1A1A1A] font-semibold" data-testid="playlist-search" />
          </div>
          <ul className="mt-4 divide-y divide-black/5 max-h-[52vh] overflow-y-auto pr-1" data-testid="playlist-tracks">
            {filtered.map((s) => (
              <li key={s.i}>
                <button onClick={() => pick(s.i)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl transition hover:bg-[#10B981]/10 ${s.i === idx ? "bg-[#10B981]/15" : ""}`}
                  data-testid={`track-${s.i}`}>
                  <span className={`w-8 text-lg font-bold ${s.i === idx ? "text-[#10B981]" : "text-[#5A5A5A]"}`}>
                    {s.i === idx && playing ? "♪" : s.i + 1}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-lg font-bold truncate ${s.i === idx ? "text-[#10B981]" : "text-[#1A1A1A]"}`}>{s.t}</span>
                    {s.a && <span className="block text-lg text-[#5A5A5A] font-semibold truncate">{s.a}</span>}
                  </span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3 py-6 text-lg text-[#5A5A5A] font-semibold">No matches — try a different search, or suggest it via Feedback!</li>}
          </ul>
        </div>
      </section>
    </PageBg>
  );
}
